/**
 * @file date-picker/subcomponents/weekday-row.tsx
 *
 * Header row displaying weekday names.
 * Locale-aware via Intl.DateTimeFormat.
 * Supports weekStartsOn = 0 (Sun) or 1 (Mon).
 */

import * as React from "react";
import { getWeekdayNames } from "../../shared/calendar-model";
import type { ResolvedDateLocale } from "../../shared/types";

interface WeekdayRowProps {
	locale: ResolvedDateLocale;
}

/**
 * Weekday header row (Mon Tue Wed Thu Fri Sat Sun).
 * Locale-aware, following weekStartsOn configuration.
 */

export const WeekdayRow: React.FC<WeekdayRowProps> = ({ locale }) => {
	const names = React.useMemo(() => getWeekdayNames(locale), [locale]);

	return (
		<tr className="grid grid-cols-7 mb-1">
			{names.map((name, i) => (
				<th
					// biome-ignore lint/suspicious/noArrayIndexKey: stable weekday order
					key={i}
					scope="col"
					aria-label={name}
					className="flex items-center justify-center h-10 w-10 mx-auto
						text-[length:var(--md-typescale-label-large-size,14px)]
						font-[number:var(--md-typescale-label-large-weight,500)]
						text-m3-on-surface
						select-none
						font-normal"
				>
					{name}
				</th>
			))}
		</tr>
	);
};

WeekdayRow.displayName = "WeekdayRow";
